import '@uiw/react-markdown-preview/markdown.css';
import MDEditor from '@uiw/react-md-editor';
import '@uiw/react-md-editor/markdown-editor.css';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import rehypeSanitize from 'rehype-sanitize';

interface SkillMarkdownEditorProps {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    disabled?: boolean;
}

/**
 * Visual markdown editor for a skill's SKILL.md body — a formatting toolbar plus a preview toggle, on
 * top of `@uiw/react-md-editor` (already used by the org portal). `data-color-mode` is driven by the app
 * theme so it matches light/dark, and preview HTML is sanitized.
 */
export default function SkillMarkdownEditor({ value, onChange, placeholder, disabled }: SkillMarkdownEditorProps) {
    const theme = useTheme();

    return (
        <Wrapper data-color-mode={theme.mode}>
            <MDEditor
                value={value}
                onChange={(next) => onChange(next ?? '')}
                height={360}
                preview="edit"
                visibleDragbar={false}
                textareaProps={{ placeholder, disabled }}
                previewOptions={{
                    disallowedElements: ['style', 'script'],
                    rehypePlugins: [[rehypeSanitize]],
                }}
            />
        </Wrapper>
    );
}

// Match the editor chrome to the app: rounded border + themed surfaces so it doesn't read as a foreign
// widget. The .w-md-editor classes come from @uiw/react-md-editor's stylesheet.
const Wrapper = styled.div`
    .w-md-editor {
        border: 1px solid ${(props) => props.theme.color.border};
        border-radius: 0.5rem;
        background-color: ${(props) => props.theme.color.card};
        box-shadow: none;
    }
    .w-md-editor-toolbar {
        background-color: ${(props) => props.theme.color.accentCard};
        border-bottom: 1px solid ${(props) => props.theme.color.border};
        border-radius: 0.5rem 0.5rem 0 0;
    }
    .w-md-editor-text,
    .w-md-editor-content {
        background-color: ${(props) => props.theme.color.card};
    }
`;
